import { UserCategory } from '../Model/User';
import { getContractEntries, isLiveContractEntry } from './userCoachData';

/**
 * The category a product places a client in. Mirrors the server's `categoryForProduct`
 * and mobile's `categoryForProduct` — the rules live in one place per repo.
 *
 * @param {object} product - A Product model instance.
 * @return {string} The category that product implies.
 */
const categoryForProduct = (product) => {
  if (product.isChallenge) return UserCategory.CHALLENGES;
  if (product.isAiFriendEnabled) return UserCategory.AI_FRIEND;
  if (product.isDefault) return UserCategory.FREE_CLIENTS;
  return UserCategory.OTHER;
};

/**
 * Product id to category, from products the caller already holds. Products with no doc
 * are left out, so an unresolved product stays distinguishable from a known one.
 *
 * @param {Array<object>} products - Product model instances.
 * @return {Map<string, string>}
 */
const categoryMap = (products) => new Map(
  (products || []).filter(Boolean).map((product) => [product.id, categoryForProduct(product)]),
);

/**
 * The products one coach's questions about a client are answered from: live entries,
 * else all of that coach's entries, else the legacy root `product`.
 *
 * The root rung is gated on the client having NO entries at all, not on this coach having
 * none — otherwise a coach with a lingering entry would be answered with another coach's
 * product. Deliberately not live-only: Manage Clients lists cancelled clients.
 *
 * @param {object} user - A User model instance.
 * @param {string} coachId - The coach whose products to resolve.
 * @return {Array<string>} Distinct product ids.
 */
const resolveCoachProductIds = (user, coachId) => {
  const contractsMap = user?.data?.contracts;
  if (!Object.keys(contractsMap || {}).length) {
    return user?.data?.product ? [user.data.product] : [];
  }
  if (!coachId) return [];
  const entries = getContractEntries(contractsMap, coachId);
  const live = entries.filter(isLiveContractEntry);
  const source = live.length ? live : entries;
  return [...new Set(source.map((contract) => contract.productId).filter(Boolean))];
};

/**
 * Every category the client holds with this coach. Unresolved products are dropped.
 *
 * @param {Map<string, string>} map - From `categoryMap`.
 * @param {object} user - A User model instance.
 * @param {string} coachId - The coach to scope to.
 * @return {Set<string>}
 */
const categoriesForCoach = (map, user, coachId) => new Set(
  resolveCoachProductIds(user, coachId).map((productId) => map.get(productId)).filter(Boolean),
);

/**
 * True only when the client has products with this coach AND every one falls in
 * `allowed`. False on an empty set and on any unresolved product, so exclusion gates
 * fail open — matching the behaviour an absent stored category used to give.
 *
 * @param {Map<string, string>} map - From `categoryMap`.
 * @param {object} user - A User model instance.
 * @param {string} coachId - The coach to scope to.
 * @param {Array<string>} allowed - The categories that count as excluded.
 * @return {boolean}
 */
const allIn = (map = new Map(), user, coachId, allowed) => {
  const productIds = resolveCoachProductIds(user, coachId);
  return productIds.length > 0
    && productIds.every((productId) => allowed.includes(map.get(productId)));
};

export {
  allIn,
  categoriesForCoach,
  categoryForProduct,
  categoryMap,
  resolveCoachProductIds,
};
